import { c as _c } from "react/compiler-runtime";
import { delay } from 'betterplace-utils';
import classNames from 'classnames';
import { type CSSProperties, useEffect, useMemo, useRef, useState } from 'react';
import styles from './AnimateNumber.module.css';
export type AnimateChildren = string | number | Iterable<string | number>;
export type AnimateNumberProps = {
  children: AnimateChildren;
  className?: string;
  style?: CSSProperties;
  animationDurationInMs?: number;
  maxNumberScale?: number;
};
export function processNumber(num: string | number) {
  const num_ = typeof num === 'number' ? num.toString() : num;
  let prefix = '';
  let suffix = '';
  const separators: string[] = [];
  const integers: string[] = [];
  let currentSegment: string = '';
  let prevCharIsNumber = false;
  const isNumber = (char: string | undefined) => typeof char === 'string' && /[0-9]/.test(char);
  for (let i = 0; i <= num_.length; i++) {
    const char = num_[i];
    const charIsNumber = isNumber(char);

    // Handle transition from non-number to number
    if (charIsNumber && !prevCharIsNumber) {
      if (!integers.length) {
        prefix = currentSegment;
      } else if (currentSegment) {
        separators.push(currentSegment);
      }
      currentSegment = '';
    }

    // Handle transition from number to non-number
    if (!charIsNumber && prevCharIsNumber) {
      integers.push(currentSegment);
      currentSegment = '';
    }

    // Handle continuing non-number
    if (!charIsNumber && !prevCharIsNumber) {
      suffix = currentSegment;
    }
    prevCharIsNumber = charIsNumber;
    currentSegment += char;
  }
  return {
    prefix,
    suffix,
    separators,
    integers
  };
}
function NumberSlot(t0) {
  const $ = _c(10);
  const {
    num,
    separator
  } = t0;
  const t1 = parseInt(num, 10);
  let t2;
  if ($[0] !== t1) {
    t2 = t1.toString();
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const numStr = t2;
  const zeroPadLength = Math.max(num.length - numStr.length, 0);
  let t3;
  if ($[2] !== zeroPadLength) {
    t3 = "0".repeat(zeroPadLength);
    $[2] = zeroPadLength;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const t4 = separator ?? "";
  let t5;
  if ($[4] !== num) {
    t5 = {
      "--number": num
    };
    $[4] = num;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const t6 = t5 as CSSProperties;
  let t7;
  if ($[6] !== t3 || $[7] !== t4 || $[8] !== t6) {
    t7 = <span data-zero-pad={t3} data-separator={t4} style={t6} />;
    $[6] = t3;
    $[7] = t4;
    $[8] = t6;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  return t7;
}
function AnimateNumber(t0) {
  const $ = _c(30);
  const {
    children,
    style,
    className,
    animationDurationInMs: t1,
    maxNumberScale: t2
  } = t0;
  const animationDurationInMs = t1 === undefined ? 1000 : t1;
  const maxNumberScale = t2 === undefined ? 2 : t2;
  const num = (Array.isArray(children) ? children[0] : children) as string | number;
  const prevValue = useRef(null);
  const [delta, setDelta] = useState(0);
  let t3;
  if ($[0] !== num) {
    t3 = processNumber(num);
    $[0] = num;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const res = t3;
  const lastIndex = res.integers.length - 1;
  let t4;
  if ($[2] !== lastIndex || $[3] !== res.integers) {
    t4 = res.integers.slice(0, lastIndex).join("");
    $[2] = lastIndex;
    $[3] = res.integers;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const value = parseFloat(t4 + (res.separators[lastIndex - 1] ? "." + res.integers[lastIndex] : res.integers[lastIndex]));
  const numberValue = value;
  let t5;
  let t6;
  if ($[5] !== animationDurationInMs || $[6] !== numberValue) {
    t5 = () => {
      const animate = async function animate() {
        if (isNaN(numberValue)) {
          return;
        }
        const prev = prevValue.current ?? 0;
        let delta_0 = numberValue - prev;
        prevValue.current = numberValue;
        if (prevValue.current === null) {
          delta_0 = 0;
        }
        setDelta(delta_0);
        await delay(animationDurationInMs);
        setDelta(0);
      };
      animate().then();
    };
    t6 = [animationDurationInMs, numberValue];
    $[5] = animationDurationInMs;
    $[6] = numberValue;
    $[7] = t5;
    $[8] = t6;
  } else {
    t5 = $[7];
    t6 = $[8];
  }
  useEffect(t5, t6);
  const scale = Math.max(Math.max(maxNumberScale - 1, 0) * ((Math.log10(delta / 100) + 3) / 4) + 1, 1);
  if (isNaN(numberValue)) {
    return null;
  }
  const {
    prefix,
    suffix,
    separators,
    integers
  } = res;
  let t7;
  if ($[9] !== className) {
    t7 = classNames(styles.number, className);
    $[9] = className;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const t8 = `${animationDurationInMs}ms`;
  let t9;
  if ($[11] !== scale || $[12] !== style || $[13] !== t8) {
    t9 = {
      ...style,
      "--number-scale": scale,
      "--number-animation-duration": t8
    };
    $[11] = scale;
    $[12] = style;
    $[13] = t8;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  const t10 = t9 as CSSProperties;
  let t11;
  if ($[15] !== integers || $[16] !== separators) {
    let t12;
    if ($[18] !== integers.length || $[19] !== separators) {
      t12 = (num_0, index) => {
        const separator = separators[index];
        const key = integers.length - 1 - index;
        return <NumberSlot key={key} num={num_0} separator={separator} />;
      };
      $[18] = integers.length;
      $[19] = separators;
      $[20] = t12;
    } else {
      t12 = $[20];
    }
    t11 = integers.map(t12);
    $[15] = integers;
    $[16] = separators;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[21] !== num) {
    t12 = <span key="sr-only" className="sr-only">{num}</span>;
    $[21] = num;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== prefix || $[24] !== suffix || $[25] !== t10 || $[26] !== t11 || $[27] !== t12 || $[28] !== t7) {
    t13 = <div className={t7} data-prefix={prefix} data-suffix={suffix} style={t10}>{t11}{t12}</div>;
    $[23] = prefix;
    $[24] = suffix;
    $[25] = t10;
    $[26] = t11;
    $[27] = t12;
    $[28] = t7;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  return t13;
}
export default AnimateNumber;